import { OverlayedLoader } from '@mews-commander/ui';
import { IconButton, IconName, Stack } from '@optimus-web/core';
import { getCustomerFullName } from '@mews-commander/legacy-entities/src/ducks/customers';
import { Companionship, useRepositoryMutation } from '@mews-commander/data-access';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { WithScope } from '@mews-commander/types';
import { lt, showSuccessAlert } from '@mews/framework';
import { deleteCompanionships as deleteCompanionshipsFromRedux } from '@mews-commander/legacy-entities/src/ducks/companionships';
import { getFirstCheckedReservationId } from '../../../selectors';
import { editProfile } from '../../../actions/profile';

interface UnassignedCompanionsListContainerProps extends WithScope {
    onSuccess?: () => void;
    companionship: Companionship;
}

export const UnassignedCompanionsListItemContainer = ({
    companionship,
    scope,
    onSuccess,
}: UnassignedCompanionsListContainerProps) => {
    const name = useCommanderSelector((state) => getCustomerFullName(state, { id: companionship.companionId }));

    const dispatch = useCommanderThunkDispatch();
    const handleEditProfile = () => {
        dispatch(editProfile({ accountId: companionship.companionId }));
    };

    const { mutate: deleteCompanionshipMutation, isPending: isLoadingDeleteCompanionship } = useRepositoryMutation(
        'companionships',
        'delete',
        {
            onSuccess: () => {
                // ToDo: Remove this when the new reservation module is fully migrated and just invalidate queries inside submit form mutations
                dispatch(
                    deleteCompanionshipsFromRedux({
                        companionshipId: companionship.id,
                    })
                );
                onSuccess?.();
                showSuccessAlert({ description: lt('CompanionRemoved') });
            },
        }
    );

    const deleteCompanionships = () => {
        deleteCompanionshipMutation({
            companionshipId: companionship.id,
        });
    };

    const { mutate: updateCompanionshipMutation, isPending: isLoadingUpdateCompanionship } = useRepositoryMutation(
        'companionships',
        'update',
        {
            onSuccess: () => {
                onSuccess?.();
                showSuccessAlert({ description: lt('AddCompanionSuccessMessage') });
            },
        }
    );

    const firstCheckedReservationId = useCommanderSelector((state) => getFirstCheckedReservationId(state, { scope }));
    const updateCompanionship = () => {
        updateCompanionshipMutation({
            companionshipId: companionship.id,
            reservationId: firstCheckedReservationId,
        });
    };

    return (
        <UnassignedCompanionsListItem
            name={name}
            isLoading={isLoadingDeleteCompanionship || isLoadingUpdateCompanionship}
            handleEditProfile={handleEditProfile}
            deleteCompanionship={deleteCompanionships}
            updateCompanionship={updateCompanionship}
        />
    );
};

interface UnassignedCompanionsListProps {
    name: string;
    isLoading: boolean;
    handleEditProfile: () => void;
    deleteCompanionship: () => void;
    updateCompanionship: () => void;
}

export const UnassignedCompanionsListItem = ({
    deleteCompanionship,
    handleEditProfile,
    isLoading,
    name,
    updateCompanionship,
}: UnassignedCompanionsListProps) => {
    return (
        <Stack itemAlignment="center" distribution="space-between">
            <OverlayedLoader show={isLoading} />
            <Stack itemAlignment="center" spacing="100">
                {name}
                <IconButton
                    type="button"
                    variant="ghost"
                    icon={IconName.Profile}
                    size="small"
                    onClick={handleEditProfile}
                />
            </Stack>
            <Stack itemAlignment="center">
                <IconButton
                    type="button"
                    variant="ghost"
                    icon={IconName.CrossClose}
                    size="small"
                    onClick={deleteCompanionship}
                />
                <IconButton
                    type="button"
                    variant="ghost"
                    icon={IconName.ChevronRight}
                    size="small"
                    onClick={updateCompanionship}
                />
            </Stack>
        </Stack>
    );
};
